import { useApolloClient } from '@apollo/client';
import { message } from 'antd';
import { Formik } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type LocalCustomerManagementModuleSpecsFragment } from '../../../../api/fragments';
import {
    UpdateLocalCustomerManagementKycFieldsDocument,
    type UpdateLocalCustomerManagementKycFieldsMutation,
    type UpdateLocalCustomerManagementKycFieldsMutationVariables,
} from '../../../../api/mutations/updateLocalCustomerManagementKYCFields';
import { type LocalCustomerFieldKey } from '../../../../api/types';
import Form from '../../../../components/fields/Form';
import useHandleError from '../../../../utilities/useHandleError';
import { useKycFieldDefinitionsFromCountry } from '../../UpdateKYCPresetPage/KYCFieldDefinitions';
import KYCFieldsForm from './KYCFieldsForm';

export type KYCFieldsProps = { module: LocalCustomerManagementModuleSpecsFragment };

export type KYCFieldFormValues = { kycFields: LocalCustomerFieldKey[] };

const KYCFields = ({ module }: KYCFieldsProps) => {
    const { t } = useTranslation('localCustomerModuleDetails');

    const definitions = useKycFieldDefinitionsFromCountry(module.company.countryCode);

    const initialValues = useMemo(
        () => ({ kycFields: [...module.kycFields].toSorted((a, b) => a.order - b.order).map(item => item.field) }),
        [module]
    );

    const apolloClient = useApolloClient();

    const onSubmit = useHandleError(
        async ({ kycFields }: KYCFieldFormValues) => {
            // submitting message
            message.loading({
                content: t('localCustomerModuleDetails:kycFieldsForm.messages.updateKYCFieldsSubmitting'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient
                .mutate<
                    UpdateLocalCustomerManagementKycFieldsMutation,
                    UpdateLocalCustomerManagementKycFieldsMutationVariables
                >({
                    mutation: UpdateLocalCustomerManagementKycFieldsDocument,
                    variables: {
                        moduleId: module.id,
                        kycFields: kycFields.map((field, index) => ({
                            field,
                            order: index + 1,
                            // TODO: temporarily set as true until FE draggable component implemented
                            isActive: true,
                        })),
                    },
                })
                .finally(() => message.destroy('primary'));

            // inform about success
            message.success({
                content: t('localCustomerModuleDetails:kycFieldsForm.messages.updateKYCFieldsSuccessful'),
                key: 'primary',
            });
        },
        [apolloClient, module.id, t, definitions]
    );

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit}>
            {({ handleSubmit }) => (
                <Form id="updateKYCFields" name="updateKYCFields" onSubmitCapture={handleSubmit}>
                    <KYCFieldsForm company={module.company} />
                </Form>
            )}
        </Formik>
    );
};

export default KYCFields;
